// Mieterportal E5: Termine, Dokumente, Mietkonto – reine Anzeige-Regeln (keine Datenzugriffe).
import { aktuellerStand, leseStand } from '../mieter/mietstaende';
import { terminTypLabel } from '../termine/typLabel';
import type { MieterKontext } from './mieter';

export interface PortalTermin {
  id: string; titel: string; typ: string; typ_freitext: string | null; start_datum: string; end_datum: string | null;
  ganztaegig: boolean | null; firma: string | null; hinweis: string | null; ticket_nummer: string | null;
}

export interface PortalDokument {
  id: string; titel: string; datei_name: string | null; kategorie: string | null; dokument_typ: string | null; quelle: string | null;
  created_at: string; datei_groesse: number | null; datei_typ: string | null; herunterladbar: boolean;
}

// ── Termine ──
export const terminTyp = (t: Pick<PortalTermin, 'typ' | 'typ_freitext'>): string => terminTypLabel({ typ: t.typ, typ_freitext: t.typ_freitext });

/** Kalenderblatt: Monat, Tag, Wochentag (lang) */
export function terminBlatt(iso: string): { monat: string; tag: string; wochentag: string } {
  const d = new Date(iso);
  return {
    monat: d.toLocaleDateString('de-DE', { month: 'short' }).replace('.', ''),
    tag: d.toLocaleDateString('de-DE', { day: '2-digit' }),
    wochentag: d.toLocaleDateString('de-DE', { weekday: 'long' }),
  };
}

const UHR = (iso: string) => new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

/** „08:00 – 12:00 Uhr“ bzw. „ganztägig“ */
export function terminZeit(t: Pick<PortalTermin, 'start_datum' | 'end_datum' | 'ganztaegig'>): string {
  if (t.ganztaegig) return 'ganztägig';
  return t.end_datum ? `${UHR(t.start_datum)} – ${UHR(t.end_datum)} Uhr` : `${UHR(t.start_datum)} Uhr`;
}

const icsDatum = (iso: string) => new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
const icsTag = (iso: string, plusTage = 0) => {
  const d = new Date(iso); d.setUTCDate(d.getUTCDate() + plusTage);
  return d.toISOString().slice(0, 10).replace(/-/g, '');
};
const icsText = (s: string) => s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');

/** Kalenderdatei (RFC 5545) für „In meinen Kalender“. Ohne Ende: 1 Stunde. */
export function icsInhalt(t: PortalTermin, ort: string, jetzt: Date = new Date()): string {
  const titel = t.titel || terminTyp(t);
  const zeilen = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Immo Schwede//Mieterportal//DE', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${t.id}@mieterportal.immo-schwede`,
    `DTSTAMP:${icsDatum(jetzt.toISOString())}`,
  ];
  if (t.ganztaegig) {
    zeilen.push(`DTSTART;VALUE=DATE:${icsTag(t.start_datum)}`, `DTEND;VALUE=DATE:${icsTag(t.end_datum ?? t.start_datum, 1)}`);
  } else {
    const ende = t.end_datum ?? new Date(new Date(t.start_datum).getTime() + 3600_000).toISOString();
    zeilen.push(`DTSTART:${icsDatum(t.start_datum)}`, `DTEND:${icsDatum(ende)}`);
  }
  zeilen.push(`SUMMARY:${icsText(titel)}`);
  if (ort) zeilen.push(`LOCATION:${icsText(ort)}`);
  const beschreibung = [t.firma ? `Ausführend: ${t.firma}` : '', t.hinweis ?? ''].filter(Boolean).join('\n');
  if (beschreibung) zeilen.push(`DESCRIPTION:${icsText(beschreibung)}`);
  zeilen.push('END:VEVENT', 'END:VCALENDAR');
  return zeilen.join('\r\n') + '\r\n';
}

export const icsDateiname = (t: Pick<PortalTermin, 'titel' | 'start_datum'>): string =>
  `${(t.titel || 'Termin').replace(/[^\p{L}\p{N} _-]/gu, '').trim().replace(/\s+/g, '-').slice(0, 60) || 'Termin'}-${t.start_datum.slice(0, 10)}.ics`;

// ── Dokumente ──
export type DokGruppe = 'mietvertrag' | 'abrechnungen' | 'protokolle' | 'haus' | 'weitere';
export const DOK_GRUPPEN: { key: DokGruppe; label: string }[] = [
  { key: 'mietvertrag', label: 'Mietvertrag' },
  { key: 'abrechnungen', label: 'Abrechnungen' },
  { key: 'protokolle', label: 'Protokolle' },
  { key: 'haus', label: 'Haus' },
  { key: 'weitere', label: 'Weitere Unterlagen' },
];

/** Gruppe aus Kategorie/Typ/Herkunft (die Kategorien im Bestand sind Freitext). */
export function dokGruppe(d: Pick<PortalDokument, 'kategorie' | 'dokument_typ' | 'quelle'>): DokGruppe {
  const k = (d.kategorie ?? '').toLowerCase();
  if (d.quelle === 'uebergabe' || d.dokument_typ === 'protokoll' || k.includes('protokoll')) return 'protokolle';
  if (d.dokument_typ === 'vertrag' || k.includes('mietvertrag') || k === 'vertrag' || k.includes('wohnungsgeber')) return 'mietvertrag';
  if (k.includes('abrechnung') || k.includes('nebenkosten') || k.includes('heizkosten')) return 'abrechnungen';
  if (k.includes('hausordnung') || k.includes('haus') || k.includes('müll') || k.includes('muell')) return 'haus';
  return 'weitere';
}

export function gruppiereDokumente(docs: PortalDokument[]): { key: DokGruppe; label: string; docs: PortalDokument[] }[] {
  return DOK_GRUPPEN.map((g) => ({ ...g, docs: docs.filter((d) => dokGruppe(d) === g.key) })).filter((g) => g.docs.length > 0);
}

export function groesseText(bytes: number | null): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
}

export const dokMeta = (d: PortalDokument): string =>
  [new Date(d.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }), groesseText(d.datei_groesse)].filter(Boolean).join(' · ');

/** Kurzkennung im Dokument-Kärtchen (PDF, JPG …) */
export function dateiKuerzel(d: Pick<PortalDokument, 'datei_name' | 'datei_typ'>): string {
  const endung = (d.datei_name ?? '').split('.').pop()?.toUpperCase() ?? '';
  if (endung && endung.length <= 4 && endung !== (d.datei_name ?? '').toUpperCase()) return endung;
  if (d.datei_typ?.includes('pdf')) return 'PDF';
  return 'DATEI';
}

// ── Mietkonto (nur Soll – Zahlungen kommen erst mit der Buchhaltung) ──
export interface MietTeil { label: string; betrag: number }

export function mietTeile(m: MieterKontext['mieter'], heute: string): MietTeil[] {
  const historie = Array.isArray(m.miet_historie) ? (m.miet_historie as unknown[]).map((r, i) => leseStand(r, i)) : [];
  const stand = aktuellerStand(historie, heute);
  const z = (v: unknown) => Number(v) || 0;
  const teile: MietTeil[] = stand
    ? [
        { label: 'Kaltmiete', betrag: stand.kaltmiete },
        { label: 'Nebenkosten-Vorauszahlung', betrag: stand.betriebskosten },
        { label: 'Heizkosten-Vorauszahlung', betrag: stand.heizkosten },
        { label: 'Weitere Kosten', betrag: stand.weitereKosten },
      ]
    : [
        { label: 'Kaltmiete', betrag: z(m.kaltmiete) },
        { label: 'Nebenkosten-Vorauszahlung', betrag: z(m.nebenkosten_vorauszahlung) },
        { label: 'Heizkosten-Vorauszahlung', betrag: z(m.heizkosten) },
        { label: 'Weitere Kosten', betrag: z(m.weitere_kosten) },
      ];
  return teile.filter((t) => t.betrag > 0);
}

/** Text zur Kaution – nur was gespeichert ist (kein Eingangsdatum vorhanden). */
export function kautionText(soll: number | null, ist: number | null, art: string | null): string {
  const s = Number(soll) || 0;
  const i = ist === null || ist === undefined ? null : Number(ist) || 0;
  const artText = art ? ` (${art})` : '';
  if (!s) return '';
  if (i === null) return `Vereinbarte Mietkaution${artText}.`;
  if (i >= s) return `Vollständig hinterlegt${artText}.`;
  if (i > 0) return `${i.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} von ${s.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} hinterlegt${artText}.`;
  return `Noch nicht hinterlegt${artText}.`;
}

export function zahlweiseText(zahlungsweise: string | null): string {
  const z = (zahlungsweise ?? '').toLowerCase();
  if (z === 'lastschrift') return 'per Lastschrift';
  if (z === 'überweisung' || z === 'dauerauftrag') return 'per Überweisung';
  if (z === 'bar') return 'bar';
  return '';
}
